import { appType } from '@/types'
import { getAppServiceCode } from '@/api/config'
import type {
    WebFormType,
    ServicePrincipal
} from '@/components/new-service/type.d'
import type { FilingAddEditType } from '@/components/filing/type.d'

import {
    individualSubject,
    electronicVerificationType,
    manualVerificationType,
    gxbVerificationType
} from '@/types/constant'
import { domainTest, wwwFrontTest } from '@/utils/regular'
import { getDuplicates } from '@/utils/util'
import { logoutAccessSource, logoutServiceSource } from '@/types/filing'
import { companyDocumentCheck, faceComparison } from '@/api/verification'

/**
 * 证件类型查询主办单位
 * @param sponsor 主办单位列表
 * @param certType 证件类型
 * @returns 查询结果
 */
export const findSponsor = (sponsor: any[], certType: string) => {
    if (!sponsor.length) {
        console.error('主办单位列表不能为空!')
    }
    const unit = sponsor.find((item) => {
        const typeIds = item.child.map((item: any) => String(item.value))
        return typeIds.includes(certType)
    })
    if (unit) {
        return unit.value
    }
    return ''
}

/**
 * 获取主办单位和证件类型
 * @param sponsor 主办单位列表
 * @param certType 证件类型
 * @returns 主办单位和证件类型
 */
export const getSponsorCertType = (sponsor: any[], certType: string) => {
    const unit = findSponsor(sponsor, certType)
    if (unit) return [unit, certType]
    return []
}

type AreaType = {
    value: number
    name: string
    level: number
}

type ReturnAreaType = {
    child?: AreaType[]
} & AreaType

/**
 * 获取下级城市
 * @param list 要过滤的列表
 * @param reange 切片范围
 * @param parentCode 上级城市
 * @returns
 */
export const filterCode = (
    list: AreaType[],
    ragne: [number, number],
    parentCode: string
): ReturnAreaType[] => {
    return list.filter((item) => {
        const { value } = item
        const code = String(value)
        const currentCode = code.slice(...ragne)
        return parentCode === currentCode
    })
}

/**
 *
 * @param address 地址分类
 * @returns
 */
export const addressGroup = (address: any[]) => {
    const { '1': provinceList, '2': cityList, '3': districtList } = address
    for (let i = 0; i < provinceList.length; i++) {
        // 省份
        const province = provinceList[i]
        const { value } = province
        const code = String(value)
        const provinceCode = code.slice(0, 2)
        // 城市
        const filterChild = filterCode(cityList, [0, 2], provinceCode)
        provinceList[i].child = filterChild
        for (let j = 0; j < filterChild.length; j++) {
            const city = filterChild[j]
            const { value } = city
            const code = String(value)
            const cityCode = code.slice(0, 4)
            // 地区
            const child = filterCode(districtList, [0, 4], cityCode)
            filterChild[j].child = child
        }
    }
    return provinceList
}

/**
 * 格式化提交的应用服务类型
 * @param appServer 要格式化的应用服务列表
 * @param serverType 应用服务类型
 * @returns 应用服务列表
 */
export const formatSubmitAppServerType = (
    appServer: any[],
    serverType: string | number
): any[] => {
    // app应用服务类型只需要子节点(转为一维数组)
    if (appType === Number(serverType)) {
        return appServer.map((item) => item[1])
    }
    return appServer
}

/**
 * 查找应用服务类型父节点
 * @param childId 子节点ID
 * @returns 父节点ID
 */
export const findAppServerParent = (
    childId: number | string,
    appServerList: any[]
): string => {
    const parentId = appServerList.find((item) => {
        const childValue = item.child.map((childItem: any) => childItem.value)
        return childValue.includes(Number(childId))
    })
    return parentId?.value || ''
}

/**
 * 格式化获取的应用服务类型
 */

export const formatGetAppServerType = async (
    appServer: any[],
    serverType: string | number
): Promise<any[]> => {
    // app应用服务类型只需要添加父节点(设置为二维数组)
    if (appType === Number(serverType)) {
        const params = {
            is_tree: 1
        }
        // 获取应用服务类型
        const list = await getAppServiceCode(params)
        const newAppServer = []
        for (let i = 0; i < appServer.length; i++) {
            const item = appServer[i]
            const parent = findAppServerParent(item, list)
            // 添加父节点
            parent !== '' && newAppServer.push([Number(parent), Number(item)])

            if (parent == '') {
                // 查找父节点失败
                console.error(`查找父节点失败:${item}`)
            }
        }
        return newAppServer
    }
    return appServer
}

/**
 * 格式化服务信息
 * @param website_body 服务列表
 * @returns 格式化结果
 */
export const formatSubmitServerData = (website_body: WebFormType[]): any[] => {
    const webBody = website_body.map((web) => {
        // 域名、邮箱、办公电话为数组类型
        const {
            website_domain,
            web_head_email,
            web_head_landline,
            sdk_service_type_id,
            web_server_type_dict,
            service_type
        } = web

        // 格式化应用服务类型
        const webServerType = formatSubmitAppServerType(
            web_server_type_dict,
            service_type
        )

        // 格式化SDK服务类型
        const sdkServerType = sdk_service_type_id.length
            ? sdk_service_type_id
            : ['']
        return {
            ...web,
            website_domain: strDomainToArray(website_domain),
            web_head_email: [web_head_email || ''],
            web_head_landline: web_head_landline ? [web_head_landline] : [],
            sdk_service_type_id: sdkServerType,
            web_server_type_dict: webServerType
        }
    })
    return webBody
}

/**
 *
 * @param domain 服务域名
 * @param serviceType 服务类型
 * @param webBodyList 服务列表
 * @returns 是否已添加
 */
export const isAddedService = (
    domain: string,
    serviceType: string,
    webBodyList: any[]
) => {
    const index = webBodyList.findIndex((item: any) => {
        const domainExist = item.website_domain.toString() === domain.toString()
        const serviceExist =
            item.service_type.toString() === serviceType.toString()
        return domainExist && serviceExist
    })
    return ~index
}

/**
 *获取服务负责人信息
 * @param serviceInfo 服务信息
 * @returns 服务负责人信息
 */
export const getServicePrincipal = (serviceInfo: any): ServicePrincipal => {
    const {
        web_head_name,
        web_head_id_type,
        web_head_number,
        webHeadIdCardTime,
        web_head_mobile,
        web_head_email,
        web_head_emergency_mobile,
        web_head_landline,
        web_communicate_type,
        web_communicate_account
    } = serviceInfo
    return {
        // 网站负责人 姓名
        web_head_name,
        // 网站负责人 有效证件类型
        web_head_id_type,
        // 网站负责人 有效证件号码
        web_head_number,
        // 身份证有效期
        webHeadIdCardTime,
        // 网站负责人 手机号码
        web_head_mobile,
        // 网站负责人 电子邮箱
        web_head_email,
        // 网站负责人 应急联系电话
        web_head_emergency_mobile,
        // 办公电话
        web_head_landline,
        // 网站负责人 即时通讯类型
        web_communicate_type,
        // 网站负责人 即时通讯账号
        web_communicate_account
    }
}

/**
 * 获取主体负责人信息
 * @param subjectInfo 主体信息
 * @returns 主体负责人信息
 */
export const getSubjectPrincipal = (subjectInfo: any): ServicePrincipal => {
    const {
        // 主体负责人 姓名
        head_name,
        // 主体负责人 有效证件类型
        head_id_type,
        // 主体负责人 有效证件号码
        head_number,
        // 证件有效期
        headIdCardTime,
        // 主体负责人 手机号码
        head_mobile,
        // 主体负责人 电子邮箱
        head_email,
        // 主体负责人 应急联系电话
        head_emergency_mobile,
        // 办公电话
        head_landline,
        // 主体负责人 即时通讯类型
        communicate_type,
        // 主体负责人 即时通讯账号
        communicate_account
    } = subjectInfo
    return {
        // 网站负责人 姓名
        web_head_name: head_name,
        // 网站负责人 有效证件类型
        web_head_id_type: head_id_type,
        // 网站负责人 有效证件号码
        web_head_number: head_number,
        // 身份证有效期
        webHeadIdCardTime: headIdCardTime,
        // 网站负责人 手机号码
        web_head_mobile: head_mobile,
        // 网站负责人 电子邮箱
        web_head_email: head_email,
        // 网站负责人 应急联系电话
        web_head_emergency_mobile: head_emergency_mobile,
        // 办公电话
        web_head_landline: head_landline,
        // 网站负责人 即时通讯类型
        web_communicate_type: communicate_type,
        // 网站负责人 即时通讯账号
        web_communicate_account: communicate_account
    }
}

/**
 * 查找服务负责人是否存在
 * @param principalList 服务负责人列表
 * @param principal 服务负责人
 * @returns 是否存在
 */
export const findServicePrincipal = (
    principalList: ServicePrincipal[],
    principal: ServicePrincipal
): boolean => {
    return !!~principalList.findIndex((item) => {
        const { web_head_name } = principal
        return item.web_head_name === web_head_name
    })
}

/**
 * 格式化主体信息
 */
export const formaitFillingData = (data: any): FilingAddEditType => {
    const {
        // 省份信息
        organizer_province_id,
        organizer_city_id,
        organizer_district_id,
        // 主办单位 名称
        organizer_name,
        // 主办单位 有效证件号码
        organizer_number,
        // 主办单位 有效证件住所
        organizer_id_address,
        // 主办单位 通信地址
        organizer_contact_address,
        // 主体负责人 姓名
        head_name,
        // 主体负责人 有效证件类型
        head_id_type,
        // 主体负责人 有效证件号码
        head_number,
        // 主体负责人 手机号码
        head_mobile,
        // 主体负责人 应急联系电话
        head_emergency_mobile,
        // 主体负责人 即时通讯类型
        communicate_type,
        // 主体负责人 即时通讯账号
        communicate_account,
        // 主体 备注（提交至管局）
        bureau_remark,
        // 主体 备注（提交至接入商）
        provider_remark,
        // 主办单位信息
        organizer_type,
        document_id,
        // 网站投资人
        invest_name,
        // 证件有效期
        head_id_card_end_time_date,
        head_id_card_start_time_date,
        // 邮箱
        head_email_arr,
        // 办公电话
        head_landline_arr
    } = data

    // 主办单位 所在地区(展示)
    const unitArea = organizer_province_id
        ? [organizer_province_id, organizer_city_id, organizer_district_id]
        : []

    // 主办单位 证件类型(展示)
    const unitNature =
        organizer_type && document_id ? [organizer_type, document_id] : []

    // 身份证有效期
    const headIdCardTime =
        head_id_card_start_time_date && head_id_card_end_time_date
            ? [head_id_card_start_time_date, head_id_card_end_time_date]
            : []
    // 办公电话
    let head_landline = head_landline_arr[0]
    head_landline = head_landline ? head_landline : ''

    // 主体负责人 电子邮箱
    let head_email = head_email_arr[0]
    head_email = head_email ? head_email : ''

    const organizer_file = ''

    return {
        unitArea,
        unitNature,
        organizer_name,
        organizer_number,
        organizer_id_address,
        organizer_contact_address,
        // 主体负责人基本情况
        head_name,
        head_id_type: head_id_type ? head_id_type.toString() : '',
        head_number,
        head_mobile,
        head_email,
        head_emergency_mobile,
        communicate_type: communicate_type.toString(),
        communicate_account,
        bureau_remark,
        provider_remark,
        invest_name,
        head_landline: head_landline || '',
        headIdCardTime,
        // 手机号验证码
        mainMobileCode: '',
        // 主体邮箱验证码
        mainEmailCode: '',
        organizer_file
    }
}

// 是否为个人主体
export const isIndividualSubject = (mainSubject: string | number): boolean => {
    return individualSubject === mainSubject.toString()
}

// 是否为电子核验
export const isElectronicVerification = (type: string | number): boolean => {
    return electronicVerificationType === type?.toString()
}

// 是否人工核验模式
export const isManualVerification = (type: string | number): boolean => {
    return manualVerificationType === type.toString()
}

// 是否工信部电子核验
export const isGxbVerification = (type: string | number): boolean => {
    return gxbVerificationType === type?.toString()
}

// 获取来源 接入或者
// export const getSource = (source_type: number) => {
//     // 接入类型
//     const accessType = logoutAccessSource.includes(source_type)
//     if (accessType) return 1
//     // 新增备案/网站类型
//     const serviceType = logoutServiceSource.includes(source_type)
//     if (serviceType) return 2
//     // 其他类型
//     return 0
// }

// 拼接域名
export const joinDomain = (str: string) => {
    if (!str) {
        return ''
    }
    const strList = str.split('\n')
    const format = strList.map((item) => item.trim()).join('\n')
    return format
}

//  格式化域名 域名列表对象转字符串
export const formatDomain = (domainInfoList: any[]): string => {
    const domainList = domainInfoList.map((item) => item.domain)
    const domainStr = domainList.join('\n')
    return domainStr
}

// 域名转列表
export const strDomainToArray = (domainStr: string) => {
    if (!domainStr) {
        return []
    }
    const domainList = domainStr.split('\n').filter((item) => {
        return item.trim()
    })
    return domainList
}

export type FileInfoType = {
    path: string
    size: number
    width: number
    height: number
}

export type CertType = {
    fileInfo: FileInfoType
    minWidth: number
    minHeight: number
    text: string
    minSize?: number
    maxSize?: number
    fixedSize?: boolean
}

const getOtherFile = (files: any) => {
    const {
        other_file_1,
        other_file_2,
        other_file_3,
        other_file_4,
        other_file_5,
        other_file_6
    } = files

    const fileList = [
        other_file_1,
        other_file_2,
        other_file_3,
        other_file_4,
        other_file_5,
        other_file_6
    ].filter((item) => item)
    return fileList
}

// 获取服务其他材料
export const getServiceOtherFile = (web_file: any) => {
    return getOtherFile(web_file)
}

// 获取其他主体材料
export const getMainOtherFile = (main_file: any) => {
    return getOtherFile(main_file)
}

// 服务图片信息
export const webServerImages = (webFileInfo: any): CertType[] => {
    if (webFileInfo?.file_kb) {
        const webFile = []
        const {
            web_head_file,
            face_capture_file,
            web_verification_file,
            domain_file,
            commitment_file
        } = webFileInfo.file_kb

        web_head_file &&
            webFile.push({
                fileInfo: web_head_file as FileInfoType,
                minWidth: 720,
                minHeight: 1280,
                text: '服务负责人证件'
            })
        face_capture_file &&
            webFile.push({
                fileInfo: face_capture_file as FileInfoType,
                minWidth: 512,
                minHeight: 512,
                minSize: 30,
                maxSize: 500,
                fixedSize: true,
                text: '当面核验拍摄照片'
            })
        web_verification_file &&
            webFile.push({
                fileInfo: web_verification_file as FileInfoType,
                minWidth: 100,
                minHeight: 100,
                text: '服务真实性核验单'
            })
        domain_file &&
            webFile.push({
                fileInfo: domain_file as FileInfoType,
                minWidth: 100,
                minHeight: 100,
                text: '域名证书'
            })
        commitment_file &&
            webFile.push({
                fileInfo: commitment_file as FileInfoType,
                minWidth: 50,
                minHeight: 50,
                text: '备案承诺书'
            })

        let otherFile = getServiceOtherFile(webFileInfo.file_kb)
        otherFile = otherFile.map((fileItem, index) => {
            const data = {
                fileInfo: fileItem as FileInfoType,
                text: `服务其他材料${index + 1}`,
                minWidth: 100,
                minHeight: 100
            }
            return data
        })
        webFile.push(...otherFile)
        return webFile
    }
    return []
}

// 服务信息预览列表
export const serverPreviewSrcList = (servier: CertType[]) => {
    return servier.map((item: CertType) => item.fileInfo.path)
}

const getYMD = (t: string) => {
    return t.split(' ')[0]
}

// 过滤服务列表
export const filterServiceList = (
    website_body: any[],
    filter_time: string | undefined
): any[] => {
    if (!filter_time) return website_body
    if (!website_body) return []
    const YMD = getYMD(filter_time)
    // 审核未通过返回全部
    if (!YMD) return website_body
    const list = website_body.filter((item) => {
        const { examine_time } = item
        const webExamineYMD = getYMD(examine_time)
        return YMD === webExamineYMD
    })
    return list
}

export const provinceAbbreviation = () => {
    return [
        '京',
        '沪',
        '津',
        '渝',
        '冀',
        '晋',
        '辽',
        '吉',
        '黑',
        '苏',
        '浙',
        '皖',
        '闽',
        '赣',
        '鲁',
        '豫',
        '鄂',
        '湘',
        '粤',
        '琼',
        '蜀',
        '黔',
        '滇',
        '陕',
        '陇',
        '青',
        '蒙',
        '桂',
        '宁',
        '新',
        '藏',
        '港',
        '澳',
        '台'
    ]
}

/**
 * 判断是否为ICP主体备案号（全国通用）
 * @param {string} icpNumber 备案号字符串
 * @returns {boolean} true-是主体备案号 false-不是主体备案号或格式错误
 */
export const isICPSubjectNumber = (icpNumber: string) => {
    if (!icpNumber || typeof icpNumber !== 'string') return false

    // 去除所有空格
    const trimmed = icpNumber.replace(/\s+/g, '')

    // 全国省份简称列表（包括直辖市、自治区等）
    // const provinceAbbrs = provinceAbbreviation()

    // 主体备案号正则：省份简称 + ICP备 + 8位数字 + 号
    const subjectPattern = new RegExp(
        // `^[${provinceAbbrs.join('')}]ICP备\\d{8,20}号$`
        /^[\u4e00-\u9fa5]ICP备\d{8,20}号$/
    )

    // 检查是否符合主体备案号格式
    return subjectPattern.test(trimmed)
}

/**
 * 判断是否为ICP网站备案号
 * @param {string} icpNumber 备案号字符串
 * @returns {boolean} true-是网站备案号 false-不是或格式错误
 */
export const isICPWebsiteNumber = (icpNumber: string) => {
    if (!icpNumber || typeof icpNumber !== 'string') return false

    // 去除所有空格
    const trimmed = icpNumber.replace(/\s+/g, '')

    // 全国省份简称列表
    // const provinceAbbrs = provinceAbbreviation()

    // 网站备案号正则：省份简称 + ICP备 + 8位数字 + 号 + - + 数字
    const websitePattern = new RegExp(/^[\u4e00-\u9fa5]ICP备\d{8,20}号-\d+$/)

    return websitePattern.test(trimmed)
}

// 拼接ICP备案号
export const joinWebIcpCode = (str: string) => {
    if (!str) {
        return ''
    }
    const strList = str.split('\n')
    const format = strList.map((item) => item.trim()).join('\n')
    return format
}

// 获取服务备案号列表的主体
export const getWebIcpCodeMainCode = (webIcpCdeArr: string[]): string[] => {
    const mainIcpArr = webIcpCdeArr.map((item: string) => {
        const mainIcp = item.split('-')[0]
        return mainIcp
    })
    const result = [...new Set(mainIcpArr)]
    return result
}

// 域名校验
export const validatorDomain = (rule: any, value: any, callback: any) => {
    const val = value.trim() || ''
    if (!val) {
        return callback(new Error('请输入域名'))
    }
    const domainList = val.split('\n')
    for (let i = 0; i < domainList.length; i++) {
        const currentDomain = domainList[i]
        if (wwwFrontTest(currentDomain)) {
            return callback(new Error(`域名：${currentDomain}不能以www.开头`))
        }
        if (!domainTest(currentDomain)) {
            return callback(new Error(`域名：${currentDomain}格式不正确`))
        }
    }
    const duplicates = getDuplicates(domainList)
    if (duplicates.length) {
        return callback(new Error(`域名：${duplicates.join('、')}重复`))
    }
    callback()
}

// 服务备案号校验
export const validatorWebIcpCdoe = (rule: any, value: any, callback: any) => {
    const val = value.trim() || ''
    if (!val) {
        return callback(new Error('请输入服务备案号'))
    }
    const webIcpCdoeList = val.split('\n')
    for (let i = 0; i < webIcpCdoeList.length; i++) {
        const currentWebIcpCdoe = webIcpCdoeList[i]

        if (!isICPWebsiteNumber(currentWebIcpCdoe)) {
            return callback(
                new Error(`服务备案号：${currentWebIcpCdoe}格式不正确`)
            )
        }
    }
    const duplicates = getDuplicates(webIcpCdoeList)
    if (duplicates.length) {
        return callback(new Error(`服务备案号：${duplicates.join('、')}重复`))
    }
    const mainIcpArr = getWebIcpCodeMainCode(webIcpCdoeList)
    if (mainIcpArr.length > 1) {
        return callback(
            new Error(`服务备案号不能来自多个主体：${mainIcpArr.join('、')}`)
        )
    }
    callback()
}

// 营业执照校验
type companyType = {
    organizer_number: string
    organizer_name: string
    head_name: string
}
export const companyDocumentCheckFun = (params: companyType) => {
    const { organizer_number, organizer_name, head_name } = params
    const data = {
        credit_no: organizer_number,
        legal_person: head_name,
        company_name: organizer_name
    }
    return companyDocumentCheck(data)
}

// 身份证人脸校验
type idCardType = {
    face_photo: string
    name: string
    id_card_no: string
}
export const faceComparisonFun = (params: idCardType) => {
    const { face_photo, name, id_card_no } = params
    const data = {
        face_photo,
        name,
        id_card_no
    }
    return faceComparison(data)
}

const WAIT_EXAMINE = 3 // 待管局审核

/**
 * 列表/详情审核状态文案
 * 仅当当前展示记录本身处于待管局审核时，才使用短信核验/人工审核阶段文案
 */
export const getFilingStatusText = (row: any): string => {
    const step = Number(row?.approval_step)
    const websiteBody = row?.website_body
    const useApprovalStep =
        (step === 2 || step === 3) &&
        (websiteBody?.length
            ? Number(websiteBody[0]?.web_status) === WAIT_EXAMINE
            : Number(row?.main_status) === WAIT_EXAMINE)

    if (useApprovalStep) {
        return row?.combin_status_text || '-'
    }
    if (websiteBody?.length) {
        return websiteBody[0]?.web_status_text || '-'
    }
    return row?.main_status_text || '-'
}
